/**
 * Панель свойств выделенного объекта: на широких экранах — справа с отступами от краёв
 * редактора. На телефоне клик по объекту показывает компактную плашку внизу экрана с названием,
 * быстрыми действиями и кнопкой «Свойства», открывающей шторку параметров только по запросу.
 */
import { useState } from "react";
import { getCatalogItem, waterMediumLabel } from "@/shared/catalog";
import { EDITOR_OVERLAY_PAINT_STYLE } from "@/shared/config/layout";
import {
	formatArea,
	formatLengthNumber,
	formatMillimeters,
	type LengthUnit,
} from "@/shared/lib/geometry";
import { findFixture, findFloorItem, itemDimensions, type PlanLayers } from "@/shared/plan";
import { useLengthUnit, useProjectStore } from "@/shared/store";
import { remove } from "@/shared/store/actions";
import { floorItemOwner, useWallsStore } from "@/shared/store/layers";
import { UIKit } from "@/shared/ui";
import {
	DrawerSection,
	SelectionKind,
	ViewMode,
	useEditorStore,
	type Selection,
} from "@/widgets/editor";
import { detectRooms, wallFaceLengths, wallLength } from "@/widgets/plan";
import { CableProperties } from "./CableProperties";
import { CommentProperties } from "./CommentProperties";
import { FixtureProperties } from "./FixtureProperties";
import { FloorItemProperties } from "./FloorItemProperties";
import { LightProperties } from "./LightProperties";
import { LightingConnectionProperties } from "./LightingConnectionProperties";
import { NodeProperties } from "./NodeProperties";
import { OpeningProperties } from "./OpeningProperties";
import { PipeProperties } from "./PipeProperties";
import { RoomProperties } from "./RoomProperties";
import { WallProperties } from "./WallProperties";

/** Краткая сводка выделенного объекта для заголовка и мобильной плашки. */
export const selectionSummary = (
	selection: Selection | null,
	layers: PlanLayers,
	unit: LengthUnit = "mm",
): { title: string; subtitle: string | null } | null => {
	if (!selection) return null;
	/** «900 × 2100 мм» — в выбранных единицах */
	const pair = (a: number, b: number) =>
		`${formatLengthNumber(a, unit)} × ${formatMillimeters(b, unit)}`;
	const graph = layers.walls;
	switch (selection.kind) {
		case SelectionKind.Wall: {
			const wall = graph.walls[selection.id];
			if (!wall) return null;
			const axis = wallLength(graph, wall);
			const faces = wallFaceLengths(graph, wall.id) ?? { clear: axis, outer: axis };
			const stageBadge = wall.demolished ? " · демонтаж" : wall.mounted ? " · монтаж" : "";
			return {
				title: wall.bearing ? "Несущая стена" : "Перегородка",
				subtitle: `${formatMillimeters(faces.clear, unit)} · ${formatMillimeters(wall.thickness, unit)}${stageBadge}`,
			};
		}
		case SelectionKind.Opening: {
			const opening = graph.openings[selection.id];
			if (!opening) return null;
			return {
				title: opening.kind === "window" ? "Окно" : "Дверь",
				subtitle: pair(opening.width, opening.height),
			};
		}
		case SelectionKind.Fixture: {
			const found = findFixture(layers, selection.id);
			if (!found) return null;
			const info = getCatalogItem(found.fixture.ref);
			return {
				title: info?.name ?? "Объект на стене",
				subtitle: `Высота ${formatMillimeters(found.fixture.elevation, unit)}`,
			};
		}
		case SelectionKind.Item: {
			const found = findFloorItem(layers, selection.id);
			if (!found) return null;
			const info = getCatalogItem(found.item.ref);
			const dims = info ? itemDimensions(found.item, info) : null;
			return {
				title: info?.name ?? "Предмет",
				subtitle: dims ? pair(dims.width, dims.depth) : null,
			};
		}
		case SelectionKind.Light: {
			const light = layers.electrical.items[selection.id];
			if (!light) return null;
			const info = getCatalogItem(light.ref);
			return {
				title: info?.name ?? "Светильник",
				subtitle: info ? pair(info.width, info.height) : null,
			};
		}
		case SelectionKind.Room: {
			const rooms = detectRooms(graph);
			const idx = rooms.findIndex((r) => r.id === selection.id);
			if (idx === -1) return null;
			return {
				title: `Помещение ${idx + 1}`,
				subtitle: formatArea(rooms[idx].area),
			};
		}
		case SelectionKind.Cable: {
			const cable = layers.wiring.items[selection.id];
			if (!cable) return null;
			return { title: "Кабель", subtitle: null };
		}
		case SelectionKind.Pipe: {
			const pipe = layers.pipes.items[selection.id];
			if (!pipe) return null;
			return {
				title: `Труба ${waterMediumLabel(pipe.medium)}`,
				subtitle: `Поворотов: ${pipe.points.length}`,
			};
		}
		case SelectionKind.LightingConnection: {
			if (!layers.wiring.connections[selection.id]) return null;
			return { title: "Связь освещения", subtitle: null };
		}
		case SelectionKind.Comment: {
			const comment = layers.comments.items[selection.id];
			if (!comment) return null;
			return {
				title: "Комментарий",
				subtitle: comment.text.trim() ? comment.text.trim().slice(0, 36) : null,
			};
		}
		default: {
			const node = graph.nodes[selection.id];
			if (!node) return null;
			return { title: "Угол стены", subtitle: `${Math.round(node.x)}, ${Math.round(node.y)}` };
		}
	}
};

/** Наполнение панели свойств для текущего выделенного объекта. */
export const SelectionPropertiesContent = () => {
	const selection = useEditorStore((state) => state.selection);
	if (!selection) return null;

	switch (selection.kind) {
		case SelectionKind.Wall:
			return <WallProperties />;
		case SelectionKind.Opening:
			return <OpeningProperties />;
		case SelectionKind.Fixture:
			return <FixtureProperties />;
		case SelectionKind.Item:
			return <FloorItemProperties />;
		case SelectionKind.Light:
			return <LightProperties />;
		case SelectionKind.Room:
			return <RoomProperties />;
		case SelectionKind.LightingConnection:
			return <LightingConnectionProperties />;
		case SelectionKind.Cable:
			return <CableProperties />;
		case SelectionKind.Pipe:
			return <PipeProperties />;
		case SelectionKind.Comment:
			return <CommentProperties id={selection.id} />;
		default:
			return <NodeProperties />;
	}
};

/**
 * Компактная плашка выбранного объекта на телефоне (`<768px`) со шторкой свойств по кнопке.
 * Позволяет смотреть 2D, Развёртки и 3D на смартфоне без перекрытия экрана при тапе по объекту.
 */
export const MobileSelectionSheet = () => {
	const selection = useEditorStore((state) => state.selection);
	const setSelection = useEditorStore((state) => state.setSelection);
	const viewMode = useEditorStore((state) => state.viewMode);
	const firstPerson = useEditorStore((state) => state.view3d.firstPerson);
	const drawer = useEditorStore((state) => state.drawer);
	const setDrawer = useEditorStore((state) => state.setDrawer);
	const isReadOnly = useEditorStore((state) => state.isReadOnly);
	const layers = useProjectStore((state) => state.project.layers);
	const pushHistory = useProjectStore((state) => state.pushHistory);
	const [sheetOpenKey, setSheetOpenKey] = useState<string | null>(null);
	const unit = useLengthUnit();

	if (selection === null || (viewMode === ViewMode.ThreeD && firstPerson)) return null;
	if (drawer !== null) return null;

	const summary = selectionSummary(selection, layers, unit);
	if (!summary) return null;

	const selectionKey = `${selection.kind}:${selection.id}`;
	const isSheetOpen = sheetOpenKey === selectionKey;
	const isSurfaceIn3D =
		viewMode === ViewMode.ThreeD &&
		(selection.kind === SelectionKind.Wall || selection.kind === SelectionKind.Room);

	const rotateFloorItem = () => {
		if (selection.kind !== SelectionKind.Item || isReadOnly) return;
		const found = findFloorItem(layers, selection.id);
		if (!found) return;
		pushHistory();
		floorItemOwner(found.item.id)
			?.getState()
			.updateFloorItem(found.item.id, { rotation: (found.item.rotation + 90) % 360 });
	};

	const deleteSelected = () => {
		if (isReadOnly) return;
		pushHistory();
		remove(selection);
		setSelection(null);
	};

	return (
		<>
			<div
				className="pointer-events-none absolute inset-x-2 bottom-2 z-30 flex justify-center md:hidden"
				style={viewMode === ViewMode.TwoD ? EDITOR_OVERLAY_PAINT_STYLE : undefined}
			>
				<div className="pointer-events-auto flex w-full max-w-md items-center gap-1.5 rounded-xl border border-border bg-surface/96 px-3 py-2 shadow-lg backdrop-blur-sm">
					<div className="min-w-0 flex-1">
						<UIKit.Text size="small" className="truncate font-semibold text-ink">
							{summary.title}
						</UIKit.Text>
						{summary.subtitle && (
							<UIKit.Text size="small" className="truncate text-muted tabular-nums">
								{summary.subtitle}
							</UIKit.Text>
						)}
					</div>

					{!isReadOnly && selection.kind === SelectionKind.Item && (
						<UIKit.Button
							type="button"
							title="Повернуть на 90°"
							className="shrink-0 px-2 py-1.5 text-xs font-medium"
							onClick={rotateFloorItem}
						>
							90°
						</UIKit.Button>
					)}

					{!isReadOnly && isSurfaceIn3D && (
						<UIKit.Button
							type="button"
							className="shrink-0 border-accent/40 bg-accent-soft px-2.5 py-1.5 text-xs font-medium text-accent"
							onClick={() => setDrawer(DrawerSection.Finishes)}
						>
							Отделка
						</UIKit.Button>
					)}

					{!isReadOnly &&
						viewMode === ViewMode.TwoD &&
						selection.kind === SelectionKind.Wall &&
						layers.walls.walls[selection.id] && (
							<UIKit.Button
								type="button"
								className={`shrink-0 px-2.5 py-1.5 text-xs font-medium ${
									layers.walls.walls[selection.id].demolished
										? "border-danger-border bg-danger-soft text-danger"
										: "text-ink"
								}`}
								onClick={() => {
									const wall = layers.walls.walls[selection.id];
									if (!wall) return;
									pushHistory();
									useWallsStore
										.getState()
										.updateWall(wall.id, { demolished: !wall.demolished, mounted: false });
								}}
							>
								{layers.walls.walls[selection.id].demolished ? "Вернуть" : "Снос"}
							</UIKit.Button>
						)}

					{!isReadOnly && (
						<UIKit.Button
							type="button"
							className="shrink-0 px-2.5 py-1.5 text-xs font-medium"
							onClick={() => setSheetOpenKey(selectionKey)}
						>
							Свойства
						</UIKit.Button>
					)}

					{!isReadOnly &&
						selection.kind !== SelectionKind.Room &&
						selection.kind !== SelectionKind.Wall && (
							<UIKit.Button
								type="button"
								aria-label="Удалить"
								title="Удалить"
								className="flex size-7 shrink-0 items-center justify-center border-danger-border bg-danger-soft p-0 text-danger"
								onClick={deleteSelected}
							>
								<UIKit.IconTrash />
							</UIKit.Button>
						)}

					<UIKit.Button
						border={false}
						aria-label="Снять выделение"
						className="flex size-7 shrink-0 items-center justify-center p-0 text-lg leading-none text-muted hover:text-ink"
						onClick={() => setSelection(null)}
					>
						×
					</UIKit.Button>
				</div>
			</div>

			{isSheetOpen && (
				<UIKit.Modal
					ariaLabel={summary.title}
					className="h-2/3 max-h-modal overflow-hidden md:hidden"
					containerClassName="md:hidden"
					onClose={() => setSheetOpenKey(null)}
				>
					<UIKit.DrawerShell label={summary.title} onClose={() => setSheetOpenKey(null)}>
						<div className="flex h-full flex-col gap-3 overflow-y-auto p-3.5">
							<SelectionPropertiesContent />
						</div>
					</UIKit.DrawerShell>
				</UIKit.Modal>
			)}
		</>
	);
};

export const EditorRightPropertiesPanel = () => (
	<>
		<DesktopPropertiesPanel />
		<MobileSelectionSheet />
	</>
);

/** Панель свойств справа — только на широком экране. */
const DesktopPropertiesPanel = () => {
	const selection = useEditorStore((state) => state.selection);
	const setSelection = useEditorStore((state) => state.setSelection);
	const viewMode = useEditorStore((state) => state.viewMode);
	const firstPerson = useEditorStore((state) => state.view3d.firstPerson);
	const overlayTopOffset = useEditorStore((state) => state.overlayTopOffset);
	const drawer = useEditorStore((state) => state.drawer);
	const layers = useProjectStore((state) => state.project.layers);

	if (selection === null || (viewMode === ViewMode.ThreeD && firstPerson)) return null;
	// Выделенного объекта уже нет (вышли из демо, открыли другой проект) — пустую панель не показываем
	if (!selectionSummary(selection, layers)) return null;
	// Стену и комнату при открытой «Отделке» настраивают в ней
	if (
		drawer === DrawerSection.Finishes &&
		(selection.kind === SelectionKind.Wall || selection.kind === SelectionKind.Room)
	) {
		return null;
	}

	return (
		<div
			role="dialog"
			aria-label="Свойства"
			className="absolute right-2 bottom-2 z-40 hidden w-properties-mobile overflow-hidden rounded-lg border border-border bg-surface shadow-lg md:block"
			style={{
				top: overlayTopOffset,
				...(viewMode === ViewMode.TwoD ? EDITOR_OVERLAY_PAINT_STYLE : {}),
			}}
		>
			<div className="relative flex h-full min-h-0 flex-col gap-3 overflow-y-auto p-4">
				<UIKit.Button
					border={false}
					className="absolute top-2 right-2 flex size-8 items-center justify-center p-0 text-ink hover:text-ink"
					aria-label="Закрыть свойства"
					onClick={() => setSelection(null)}
				>
					<UIKit.Text as="span" className="text-2xl leading-none">
						×
					</UIKit.Text>
				</UIKit.Button>
				<SelectionPropertiesContent />
			</div>
		</div>
	);
};
